import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import {
  Box,
  Button,
  ButtonBase,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Link,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import React from 'react';

import { getSlideThumbnailUrl, getSlideOriginalUrl } from 'utils/helpers';
import { useLabs } from 'utils/queryHooks/useLabs';

const inlineThumbnailSx = {
  position: 'relative',
  width: 24,
  height: 24,
  overflow: 'hidden',
  borderRadius: 0.75,
  border: '1px solid',
  borderColor: 'divider',
  backgroundColor: 'background.default',
  flexShrink: 0,
  '& img': { width: '100%', height: '100%', objectFit: 'cover' },
} as const;

const previewImageSx = {
  position: 'relative',
  width: '100%',
  minHeight: 320,
  overflow: 'hidden',
  borderRadius: 2,
  border: '1px solid',
  borderColor: 'divider',
  backgroundColor: 'background.default',
  '& img': { width: '100%', height: '100%', objectFit: 'contain' },
} as const;

interface SlideLinkWithPreviewProps {
  slideid?: string;
  labid?: string;
  href?: string;
  children?: React.ReactNode;
}

const SlideLinkWithPreview: React.FC<SlideLinkWithPreviewProps> = ({ slideid, labid, href, children }) => {
  const [previewOpen, setPreviewOpen] = React.useState(false);
  const [thumbnailError, setThumbnailError] = React.useState(false);
  const [previewError, setPreviewError] = React.useState(false);
  const { data: labs = [] } = useLabs();

  const displayName = typeof children === 'string' ? children : slideid || 'Slide';

  const slideStub = React.useMemo(() => (slideid && labid ? { id: slideid, labId: labid } : null), [labid, slideid]);
  const isStagingLab = Boolean(labs.find((lab) => lab.id === labid)?.isStagingLab);

  const thumbnailUrl = slideStub ? getSlideThumbnailUrl(slideStub, { isStagingLab }) : null;
  const previewUrl = slideStub ? getSlideOriginalUrl(slideStub, { isStagingLab }) : null;

  if (!slideid) {
    return (
      <Link href={href} target="_blank" rel="noopener noreferrer">
        {children}
      </Link>
    );
  }

  return (
    <>
      <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, verticalAlign: 'middle' }}>
        {thumbnailUrl && !thumbnailError ? (
          <Tooltip title="Preview slide">
            <ButtonBase
              onClick={() => setPreviewOpen(true)}
              aria-label={`Preview slide: ${displayName}`}
              sx={{ borderRadius: 0.75, display: 'inline-flex', verticalAlign: 'middle' }}
            >
              <Box sx={inlineThumbnailSx}>
                <img src={thumbnailUrl} alt={`Thumbnail for ${displayName}`} onError={() => setThumbnailError(true)} />
              </Box>
            </ButtonBase>
          </Tooltip>
        ) : null}
        <Link href={href} target="_blank" rel="noopener noreferrer" underline="hover" sx={{ verticalAlign: 'middle' }}>
          {children}
        </Link>
      </Box>

      <Dialog open={previewOpen} onClose={() => setPreviewOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Slide: {displayName}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            {previewUrl && !previewError ? (
              <Box sx={previewImageSx}>
                <img src={previewUrl} alt={`Preview of ${displayName}`} onError={() => setPreviewError(true)} />
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary">
                Preview not available.
              </Typography>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewOpen(false)}>Close</Button>
          {href ? (
            <Button
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              variant="outlined"
              endIcon={<OpenInNewIcon fontSize="small" />}
            >
              Open in viewer
            </Button>
          ) : null}
        </DialogActions>
      </Dialog>
    </>
  );
};

export default SlideLinkWithPreview;
